/**
 * Registration page with form validation
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, Check } from 'lucide-react';
import { Button, Input, Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui';
import { useAuthStore, useLanguage } from '@/store';
import { getAuthTranslation, getAuthErrorTranslation, type AuthTranslationKey } from '@/utils/translations';
import { isPasswordTooLong } from '@/utils/password';
import { legalUrl } from '@/utils/legal';
import { ROUTES } from '@/config/routes';
import { safeRedirect, withRedirect } from '@/utils/redirect';

/** Replaces the {terms} and {privacy} placeholders of a translated sentence with the given nodes. */
function fillPlaceholders(template: string, nodes: Record<'terms' | 'privacy', ReactNode>): ReactNode[] {
  return template.split(/(\{terms\}|\{privacy\})/).map((part, index) => {
    if (part === '{terms}') return <span key={index}>{nodes.terms}</span>;
    if (part === '{privacy}') return <span key={index}>{nodes.privacy}</span>;
    return part;
  });
}

const ERROR_ID = 'register-error';

export function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // A5: back to the page that sent the visitor here (validated, same origin), else Početna.
  const redirect = safeRedirect(searchParams.get('redirect'));
  const language = useLanguage();
  const { register, loginWithGoogle, isLoading, error, clearError } = useAuthStore();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Helper function for translations
  const t = (key: AuthTranslationKey) => getAuthTranslation(key, language);

  const passwordRequirements = [
    {
      test: (p: string) => p.length >= 8,
      label: t('atLeast8Characters'),
    },
    {
      test: (p: string) => /[A-Z]/.test(p),
      label: t('oneUppercaseLetter'),
    },
    {
      test: (p: string) => /[a-z]/.test(p),
      label: t('oneLowercaseLetter'),
    },
    {
      test: (p: string) => /[0-9]/.test(p),
      label: t('oneNumber'),
    },
  ];

  const validateForm = (): boolean => {
    const errors: string[] = [];

    if (name.trim().length < 2) {
      errors.push(t('nameMustBe2Characters'));
    }

    if (!passwordRequirements.every((req) => req.test(password))) {
      errors.push(t('passwordDoesNotMeetRequirements'));
    }

    if (isPasswordTooLong(password)) {
      errors.push(t('passwordTooLong'));
    }

    if (password !== confirmPassword) {
      errors.push(t('passwordsDoNotMatch'));
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();

    if (!validateForm()) {
      return;
    }

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const success = await register(trimmedEmail, password, trimmedName);
    if (success) {
      navigate(redirect ?? ROUTES.home);
    }
  };

  const hasError = !!(error || validationErrors.length > 0);

  const handleGoogleLogin = async () => {
    clearError();
    await loginWithGoogle(redirect);
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">
            {t('createAccount')}
          </CardTitle>
          <CardDescription>
            {t('startFreeTrial')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" aria-describedby={hasError ? ERROR_ID : undefined}>
            {hasError && (
              <div
                id={ERROR_ID}
                role="alert"
                className="p-3 rounded-md bg-destructive/10 text-destructive text-sm"
              >
                {error ? getAuthErrorTranslation(error, language) : validationErrors.join('. ')}
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="name" className="text-sm font-medium">
                {t('fullName')}
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder={t('enterYourName')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10"
                  required
                  autoComplete="name"
                  autoFocus
                  aria-invalid={hasError}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                {t('email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t('enterYourEmail')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  autoComplete="email"
                  aria-invalid={hasError}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium">
                {t('password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('createPassword')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  required
                  autoComplete="new-password"
                  aria-invalid={hasError}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {/* Password requirements */}
              <div className="mt-2 space-y-1">
                {passwordRequirements.map((req) => (
                  <div
                    key={req.label}
                    className={`flex items-center text-xs ${
                      req.test(password) ? 'text-green-600' : 'text-muted-foreground'
                    }`}
                  >
                    <Check className={`h-3 w-3 mr-1 ${req.test(password) ? 'opacity-100' : 'opacity-30'}`} />
                    {req.label}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="text-sm font-medium">
                {t('confirmPassword')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('confirmYourPassword')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  required
                  autoComplete="new-password"
                  aria-invalid={hasError}
                />
              </div>
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-destructive">
                  {t('passwordsDoNotMatch')}
                </p>
              )}
            </div>

            <Button type="submit" className="w-full" isLoading={isLoading}>
              {t('createAccount')}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t('orContinueWith')}
              </span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleGoogleLogin}
            disabled={isLoading}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Google
          </Button>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {t('alreadyHaveAccount')}{' '}
            <Link to={withRedirect(ROUTES.login, redirect)} className="font-medium text-primary hover:underline">
              {t('signIn')}
            </Link>
          </p>

          <p className="mt-4 text-center text-xs text-muted-foreground">
            {fillPlaceholders(t('termsAndPrivacy'), {
              terms: (
                <a
                  href={legalUrl('terms', language)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  {t('termsLink')}
                </a>
              ),
              privacy: (
                <a
                  href={legalUrl('privacy', language)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-foreground"
                >
                  {t('privacyLink')}
                </a>
              ),
            })}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
